import { getCurrentSettings, getMicrophoneDeviceId } from './settings.js';
import {getCurrentNote, stopAllAudio, isCurrentChord, getCurrentNoteIndex} from './notes.js';
import { handleDetectedNote, getSpeedFactor } from "./game.js";
import {
    initializeGuitarEffects,
    disconnectEffects,
    guitarEffects,
} from './guitarEffects.js';
import { noteNameToFrequency } from './utils.js';
import { translate } from './translation.js';

class Tuner {
    constructor() {
        this.frequencyDisplay = document.getElementById('frequency');
        this.noteDisplay = document.getElementById('note');
        this.needle = document.getElementById('needle');
        this.accuracyFillLeft = document.getElementById('accuracyFillLeft');
        this.accuracyFillRight = document.getElementById('accuracyFillRight');
        this.micBtn = document.getElementById('micBtn');
        this.stopBtn = document.getElementById('stopBtn');
        this.stringButtonsContainer = document.getElementById('stringButtonsContainer');
        this.stringButtons = [];
        this.autoButton = null;
        this.targetNoteDisplay = document.getElementById('targetNote');
        this.deviationDisplay = document.getElementById('deviation');

        this.audioContext = null;
        this.analyser = null;
        this.microphone = null;
        this.monitorGain = null;
        this.isListening = false;
        this.animationId = null;
        this.currentNoteId = -1;
        this.awaitingSilence = false;
        this.mustAwaitSilence = false; // Принудительное ожидание тишины (игнорирует оптимизации)
        this.lastSignalLevel = 0;
        this.SILENCE_THRESHOLD = 0.01;
        this.MIN_VOLUME_THRESHOLD = 0.02; // Минимальный порог громкости для засчитывания ноты (защита от ложных срабатываний)
        this.gameMode = false;
        this.selectedStringIndex = null; // Индекс выбранной струны (0-5) или null для авто
        this.autoMode = true;
        this.tunedStrings = new Set();
        this.stringStableCounters = {};
        this.lastDetectedFrequency = 0;

        this.SMOOTHING_WINDOW_SIZE = 30; // Увеличено для большей стабильности
        this.frequencyHistory = [];
        this.noteHistory = []; // История определенных нот для стабилизации
        this.NOTE_STABILITY_THRESHOLD = 0.7; // Порог для стабильного определения ноты (70% совпадений)
        this.lastStableNote = null; // Последняя стабильно определенная нота
        this.noteStabilityCounter = 0; // Счетчик стабильности текущей ноты
        this.checkIntervalId = null;

        // Создаем кнопки струн при инициализации
        this.updateStringButtons();
        
        // Слушаем изменения строя гитары
        const tuningSelect = document.getElementById('tuning');
        if (tuningSelect) {
            tuningSelect.addEventListener('change', () => {
                this.updateStringButtons();
            });
        }
    }

    // Обновление кнопок струн
    updateStringButtons() {
        if (!this.stringButtonsContainer) return;

        const settings = getCurrentSettings();
        // Порядок струн: от 1-й (E4) до 6-й (E2)
        const stringNotes = settings.stringNotes || ['E4', 'B3', 'G3', 'D3', 'A2', 'E2'];

        this.stringButtonsContainer.innerHTML = '';
        this.stringButtons = [];
        this.tunedStrings.clear();
        this.stringStableCounters = {};

        // Кнопка автоматического режима
        const autoBtn = document.createElement('button');
        autoBtn.className = 'string-button auto-button active';
        autoBtn.textContent = translate('Авто');
        autoBtn.addEventListener('click', () => {
            this.autoMode = true;
            this.selectedStringIndex = null;
            this.updateStringButtonsActiveState();
        });
        this.stringButtonsContainer.appendChild(autoBtn);
        this.autoButton = autoBtn;

        // Кнопки струн: от 6-й до 1-й (слева направо)
        for (let i = stringNotes.length - 1; i >= 0; i--) {
            const btn = document.createElement('button');
            btn.className = 'string-button';

            const stringNumber = i + 1; // 6..1
            const noteName = stringNotes[i];

            btn.dataset.stringIndex = String(i);

            // Формируем текст струны с учетом перевода
            let stringLabel;
            if (window.translator && window.translator.targetLang === 'en') {
                // Для английского используем правильные суффиксы
                const suffixes = ['', 'st', 'nd', 'rd', 'th', 'th', 'th'];
                stringLabel = stringNumber === 1 ? '1st' : stringNumber === 2 ? '2nd' : stringNumber === 3 ? '3rd' : `${stringNumber}th`;
            } else {
                stringLabel = `${stringNumber}-я`;
            }
            
            btn.innerHTML = `<span class="string-label">${stringLabel}</span> <span class="note-label">${noteName}</span>`;

            btn.addEventListener('click', () => {
                this.autoMode = false;
                this.selectedStringIndex = i;
                this.updateStringButtonsActiveState();

                // При ручном выборе сразу обновим отображение, если есть последняя частота
                if (this.lastDetectedFrequency > 0) {
                    const noteInfo = this.frequencyToNote(this.lastDetectedFrequency);
                    this.updateTunerDisplay(this.lastDetectedFrequency, noteInfo.expectedFrequency);
                }
            });

            this.stringButtonsContainer.appendChild(btn);
            this.stringButtons.push(btn);
        }

        this.updateStringButtonsActiveState();
    }

    updateStringButtonsActiveState() {
        if (this.autoButton) {
            this.autoButton.classList.toggle('active', this.autoMode);
        }

        this.stringButtons.forEach((btn) => {
            const idx = parseInt(btn.dataset.stringIndex, 10);
            const isActive = !this.autoMode && this.selectedStringIndex === idx;
            btn.classList.toggle('active', isActive);
            if (this.autoMode) {
                btn.classList.remove('suggested');
            }
        });
    }

    // Инициализация тюнера
    async initTuner(gameMode = false) {
        // Проверяем, является ли устройство мобильным
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                        (window.matchMedia && window.matchMedia('(max-width: 768px)').matches);
        
        if (isMobile) {
            console.log('🚫 Микрофон отключен на мобильных устройствах');
            this.isActive = false;
            this.isListening = false;
            return;
        }
        
        const settings = getCurrentSettings();

        // Обновляем кнопки струн при инициализации
        this.updateStringButtons();

        // Если это игровой режим, убедимся что эффекты инициализированы
        if (gameMode && guitarEffects) {
            // Передаем текущий audioContext в эффекты
            await guitarEffects.initialize(this.audioContext);
        }

        await this.initAudio(settings, gameMode);
    }

    // Безопасное получение доступа к микрофону
    async getUserMedia(audioConstraints) {
        // Проверяем поддержку MediaDevices API
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            throw new Error('MediaDevices API не поддерживается в этом браузере. Убедитесь, что вы используете современный браузер и загружаете страницу по HTTPS (или через localhost).');
        }

        try {
            return await navigator.mediaDevices.getUserMedia({
                audio: audioConstraints
            });
        } catch (error) {
            throw error;
        }
    }

    // Инициализация аудио
    async initAudio(settings, gameMode = false) {
        try {
            const AudioContext = window.AudioContext || window.webkitAudioContext;

            // Убедимся, что audioContext создан и сохранен
            if (!this.audioContext || this.audioContext.state === 'closed') {
                this.audioContext = new AudioContext();
                console.log('AudioContext created:', this.audioContext);
            }

            // Проверим, что audioContext существует перед вызовом resume
            if (!this.audioContext) {
                throw new Error('Не удалось создать AudioContext');
            }

            // Добавим проверку состояния
            console.log('AudioContext state before resume:', this.audioContext.state);

            if (this.audioContext.state === 'suspended') {
                await this.audioContext.resume();
                console.log('AudioContext resumed, new state:', this.audioContext.state);
            }

            // Получаем выбранное устройство микрофона из настроек
            const deviceId = getMicrophoneDeviceId();
            const audioConstraints = {
                echoCancellation: false,
                noiseSuppression: false,
                autoGainControl: false,
                channelCount: 1,
                sampleRate: 44100
            };

            // Если выбрано конкретное устройство, добавляем его ID
            // Пробуем использовать exact для точного выбора, если не получится - используем ideal
            if (deviceId) {
                audioConstraints.deviceId = { exact: deviceId };
            }

            let stream;
            try {
                stream = await this.getUserMedia(audioConstraints);
            } catch (error) {
                // Если не удалось получить доступ к выбранному устройству (exact),
                // пробуем с ideal как fallback
                if (deviceId && error.name === 'OverconstrainedError') {
                    console.warn('Выбранное устройство недоступно, пытаемся использовать ideal:', error);
                    audioConstraints.deviceId = { ideal: deviceId };
                    try {
                        stream = await this.getUserMedia(audioConstraints);
                    } catch (fallbackError) {
                        // Если и ideal не сработал, пробуем без указания устройства
                        console.warn('Не удалось использовать выбранное устройство, используем устройство по умолчанию:', fallbackError);
                        delete audioConstraints.deviceId;
                        stream = await this.getUserMedia(audioConstraints);
                    }
                } else {
                    throw error;
                }
            }

            console.log('Microphone stream obtained');

            // Создаем анализатор для тюнера
            this.analyser = this.audioContext.createAnalyser();
            this.analyser.fftSize = settings.bufferSize;

            // Подключаем микрофон к анализатору (чистый звук для тюнера)
            this.microphone = this.audioContext.createMediaStreamSource(stream);
            this.microphone.connect(this.analyser);

            // Подключаем эффекты к микрофону (в обоих режимах)
            if (guitarEffects) {
                await guitarEffects.connectMicrophone(stream, this.audioContext);
            } else {
                await initializeGuitarEffects(stream, this.audioContext);
            }

            // В режиме настроек применяем настройку "Отключить эффекты"
            if (!gameMode) {
                const settings = getCurrentSettings();
                if (settings.disableEffects) {
                    // Если эффекты отключены, используем чистый звук
                    if (guitarEffects && guitarEffects.toggleEffects) {
                        guitarEffects.toggleEffects(false);
                    }
                } else {
                    // Если эффекты включены, используем эффекты
                    if (guitarEffects && guitarEffects.toggleEffects) {
                        guitarEffects.toggleEffects(true);
                    }
                }
            } else {
                // В игровом режиме (включая режим "проиграть все") всегда включаем эффекты
                // чтобы normalizeGuitar работала
                if (guitarEffects && guitarEffects.toggleEffects) {
                    guitarEffects.toggleEffects(true);
                }
            }

            this.micBtn.disabled = true;
            this.stopBtn.disabled = false;
            this.isListening = true;
            this.currentNoteId = -1;
            this.gameMode = gameMode;
            console.log('Tuner initialized successfully');

            if (gameMode) {
                this.checkSound();
            } else {
                this.analyzeSound(settings.sensitivity);
            }

        } catch (error) {
            console.error('Ошибка доступа к микрофону:', error);
            this.micBtn.disabled = false;

            // Более информативное сообщение об ошибке
            let errorMessage = `Ошибка: ${error.message}`;
            if (error.name === 'NotAllowedError') {
                errorMessage += '\n\nРазрешите доступ к микрофону в настройках браузера.';
            } else if (error.name === 'NotFoundError') {
                errorMessage += '\n\nВыбранный микрофон не найден. Проверьте подключение или выберите другое устройство в настройках.';
            } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
                errorMessage += '\n\nНе удалось получить доступ к выбранному микрофону. Возможно, он используется другим приложением.';
            }
            alert(errorMessage);
        }
    }

    checkSound() {
        if (!this.isListening) {
            console.log("checkSound: прослушивание отключено");
            return;
        }

        const settings = getCurrentSettings();
        const sensitivity = settings.sensitivity;
        const speedFactor = (this.gameMode && typeof getSpeedFactor === 'function') ? getSpeedFactor() : 0;

        this.analyzeNotes(this.analyser, sensitivity, async (result) => {
            if (!result) {
                this.checkSilenceGate(speedFactor);
                return;
            }

            // Проверяем режим "проиграть все" - обрабатываем попадания через активные ноты на грифе
            if (window.isPlayAllMode && typeof window.handlePlayAllNoteDetection === 'function') {
                window.handlePlayAllNoteDetection(result);
                return; // В режиме "проиграть все" не используем обычную логику
            }

            const currentNoteIndex = getCurrentNoteIndex();
            const currentNote = getCurrentNote();

            // Проверяем, что currentNote существует и является массивом
            if (!currentNote || !Array.isArray(currentNote) || currentNote.length === 0) {
                return;
            }

            const isChordStep = isCurrentChord();

            // Для одиночных нот заранее считаем ожидаемую частоту
            let expectedFrequency = null;
            if (!result.isChord && !isChordStep && currentNote.length === 1 && currentNote[0] && currentNote[0].frequency) {
                expectedFrequency = currentNote[0].frequency;
            }

            if (this.awaitingSilence) {
                this.checkSilenceGate(speedFactor);

                if (this.awaitingSilence) {
                    // Если включен принудительный режим ожидания тишины, блокируем все оптимизации
                    if (this.mustAwaitSilence) {
                        return;
                    }
                    
                    if (!isChordStep && expectedFrequency) {
                        // Новая логика: если уже звучит нужная одиночная нота,
                        // разрешаем засчитать её даже при отсутствии полной тишины
                        const normalizedFreq = this.normalizeFrequencyToTarget(result.frequency, expectedFrequency);
                        if (this.isNoteMatch(normalizedFreq, expectedFrequency)) {
                            this.awaitingSilence = false;
                            this.mustAwaitSilence = false;
                        } else if (speedFactor > 0.5 && this.lastSignalLevel < this.SILENCE_THRESHOLD * 2) {
                            // На высокой скорости дополнительно ослабляем порог по уровню сигнала
                            this.awaitingSilence = false;
                            this.mustAwaitSilence = false;
                        } else {
                            return;
                        }
                    } else {
                        // Для аккордов и остальных случаев оставляем старую механику
                        // На высокой скорости сокращаем время ожидания тишины
                        if (speedFactor > 0.5) {
                            // На скорости > 0.5 разрешаем распознавание даже при небольшом сигнале
                            if (this.lastSignalLevel < this.SILENCE_THRESHOLD * 2) {
                                this.awaitingSilence = false;
                                this.mustAwaitSilence = false;
                            } else {
                                return;
                            }
                        } else {
                            return;
                        }
                    }
                }
            }

            // Защита от ложных срабатываний: проверяем минимальный уровень громкости
            // Игнорируем очень тихие звуки (например, от зажатия струны без удара медиатором)
            if (this.lastSignalLevel < this.MIN_VOLUME_THRESHOLD) {
                return;
            }

            // Проверяем, не обработали ли мы уже эту ноту
            if (this.currentNoteId === currentNoteIndex) {
                return;
            }

            if (result.isChord) {
                // Для аккордов проверяем, что это действительно аккорд
                if (currentNote.length > 1 && result.isCorrect) {
                    this.currentNoteId = currentNoteIndex;
                    // На высокой скорости не ждем полной тишины
                    this.awaitingSilence = speedFactor <= 0.5;
                    this.mustAwaitSilence = this.awaitingSilence; // Сбрасываем принудительный режим только если awaitingSilence = false
                    // Делаем обработку неблокирующей на высокой скорости
                    if (speedFactor > 0.5) {
                        handleDetectedNote(this.currentNoteId).catch(err => console.error('Error handling note:', err));
                    } else {
                        await handleDetectedNote(this.currentNoteId);
                    }
                }
            } else {
                // Для одиночных нот проверяем совпадение частоты
                if (expectedFrequency) {
                    // Нормализуем найденную частоту к ожидаемой (исправляем сдвиг октавы/гармоники)
                    const normalizedFreq = this.normalizeFrequencyToTarget(result.frequency, expectedFrequency);

                    if (this.isNoteMatch(normalizedFreq, expectedFrequency)) {
                        this.currentNoteId = currentNoteIndex;
                        // На высокой скорости не ждем полной тишины
                        this.awaitingSilence = speedFactor <= 0.5;
                        this.mustAwaitSilence = this.awaitingSilence; // Сбрасываем принудительный режим только если awaitingSilence = false
                        // Делаем обработку неблокирующей на высокой скорости
                        if (speedFactor > 0.5) {
                            handleDetectedNote(this.currentNoteId).catch(err => console.error('Error handling note:', err));
                        } else {
                            await handleDetectedNote(this.currentNoteId);
                        }
                    }
                }
            }
        });

        // На высоких скоростях используем более частые проверки
        // requestAnimationFrame ограничен ~60 FPS, что недостаточно для быстрого определения нот
        if (this.gameMode && typeof getSpeedFactor === 'function') {
            // На очень высоких скоростях (speedFactor >= 0.75) используем интервал 5-6 мс (~160-200 FPS)
            // На высоких скоростях (speedFactor > 0.5) используем интервал 8 мс (~125 FPS)
            // На средних скоростях используем 10 мс (~100 FPS)
            // На низких скоростях используем 16 мс (~60 FPS)
            const checkInterval = speedFactor >= 0.75 ? 5 : (speedFactor > 0.5 ? 8 : (speedFactor > 0 ? 10 : 16));
            
            // Останавливаем предыдущий интервал, если он был
            if (this.checkIntervalId) {
                clearTimeout(this.checkIntervalId);
            }
            
            this.checkIntervalId = setTimeout(() => this.checkSound(), checkInterval);
        } else {
            // В неигровом режиме используем requestAnimationFrame
            this.animationId = requestAnimationFrame(() => this.checkSound());
        }
    }

    resetCurrentNoteId() {
        this.currentNoteId = -1;
    }

    checkSilenceGate(speedFactor = 0) {
        if (!this.awaitingSilence) {
            return;
        }

        // На высокой скорости используем более мягкий порог тишины
        const silenceThreshold = speedFactor > 0.5 
            ? this.SILENCE_THRESHOLD * 1.5 
            : this.SILENCE_THRESHOLD;

        if (this.lastSignalLevel < silenceThreshold) {
            this.awaitingSilence = false;
            this.mustAwaitSilence = false; // Сбрасываем принудительный режим
            this.currentNoteId = -1;
        }
    }

    // Анализ звука с микрофона - ИСПРАВЛЕННАЯ ВЕРСИЯ
    analyzeSound(sensitivity) {
        if (!this.isListening) return;

        this.analyzePitch(this.analyser, sensitivity, (result) => {
            if (!result) {
                this.frequencyHistory = [];
                this.noteHistory = [];
                this.noteStabilityCounter = 0;
                this.frequencyDisplay.textContent = '-- Hz';
                this.noteDisplay.textContent = '--';
                this.updateTunerDisplay(0);
                return;
            }

        this.frequencyHistory.push(result.frequency);

            if (this.frequencyHistory.length > this.SMOOTHING_WINDOW_SIZE) {
                this.frequencyHistory.shift();
            }

            // Используем медиану вместо среднего для более стабильного результата
            const sortedFreqs = [...this.frequencyHistory].sort((a, b) => a - b);
            const mid = Math.floor(sortedFreqs.length / 2);
            let avgFrequency = sortedFreqs.length % 2 === 0 
                ? (sortedFreqs[mid - 1] + sortedFreqs[mid]) / 2 
                : sortedFreqs[mid];

            // Определяем целевую частоту и название ноты
            const settings = getCurrentSettings();
            let targetFrequency = null;
            let noteName = '--';

            if (this.selectedStringIndex !== null && settings.stringNotes) {
                // Ручной выбор струны: используем её частоту
                const stringNote = settings.stringNotes[this.selectedStringIndex];
                if (stringNote) {
                    targetFrequency = noteNameToFrequency(stringNote);
                    noteName = stringNote;
                    // Нормализуем частоту к целевой струне для исправления ошибок детектора
                    avgFrequency = this.normalizeFrequencyToTarget(avgFrequency, targetFrequency);
                }
            } else if (Array.isArray(settings.stringNotes) && settings.stringNotes.length > 0) {
                // Авто-режим: проверяем, не близка ли частота к одной из открытых струн
                // Если да - используем именно эту струну (приоритет над автоматическим определением)
                let nearestStringIdx = null;
                let nearestStringCents = Infinity;
                const STRING_MATCH_THRESHOLD = 35; // центов - если ближе, считаем что это эта струна
                
                for (let i = 0; i < settings.stringNotes.length; i++) {
                    const f = noteNameToFrequency(settings.stringNotes[i]);
                    if (!f || f <= 0) continue;
                    const cents = 1200 * Math.log2(avgFrequency / f);
                    const abs = Math.abs(cents);
                    if (abs < nearestStringCents) {
                        nearestStringCents = abs;
                        nearestStringIdx = i;
                    }
                }
                
                // Если частота очень близка к одной из струн, используем её
                if (nearestStringIdx !== null && nearestStringCents < STRING_MATCH_THRESHOLD) {
                    targetFrequency = noteNameToFrequency(settings.stringNotes[nearestStringIdx]);
                    noteName = settings.stringNotes[nearestStringIdx];
                    // Нормализуем частоту к целевой струне, чтобы исправить небольшие ошибки детектора
                    avgFrequency = this.normalizeFrequencyToTarget(avgFrequency, targetFrequency);
                } else {
                    // Иначе используем обычное определение ноты с стабилизацией
                    const noteInfo = this.getStableNote(avgFrequency);
                    targetFrequency = noteInfo.expectedFrequency;
                    noteName = noteInfo.name;
                }
            } else {
                // Фоллбек: обычное определение ноты с стабилизацией
                const noteInfo = this.getStableNote(avgFrequency);
                targetFrequency = noteInfo.expectedFrequency;
                noteName = noteInfo.name;
            }

            this.lastDetectedFrequency = avgFrequency;

            this.frequencyDisplay.textContent = avgFrequency.toFixed(1) + ' Hz';
            this.noteDisplay.textContent = noteName;
            
            this.updateTunerDisplay(avgFrequency, targetFrequency);
        });

        this.animationId = requestAnimationFrame(() => this.analyzeSound(sensitivity));
    }

    // Обновление индикатора настройки
    updateTunerDisplay(detectedFreq, expectedFreq) {
        const settings = getCurrentSettings();
        
        // Если выбрана конкретная струна, используем её частоту
        let targetFrequency = expectedFreq;
        let targetNoteName = '';
        
        if (this.selectedStringIndex !== null && settings.stringNotes) {
            const stringNote = settings.stringNotes[this.selectedStringIndex];
            if (stringNote) {
                targetFrequency = noteNameToFrequency(stringNote);
                targetNoteName = stringNote;
            }
        }
        
        // Обновляем дисплей
        this.updateTunerDisplayWithTarget(detectedFreq, targetFrequency, targetNoteName);
    }

    // Вспомогательный метод для обновления дисплея с целевой частотой
    updateTunerDisplayWithTarget(detectedFreq, targetFrequency, targetNoteName) {
        // Если струна выбрана, но звука нет, показываем целевую ноту
        if (this.selectedStringIndex !== null && (!detectedFreq || detectedFreq <= 0)) {
            const settings = getCurrentSettings();
            if (settings.stringNotes && settings.stringNotes[this.selectedStringIndex]) {
                const stringNote = settings.stringNotes[this.selectedStringIndex];
                if (this.targetNoteDisplay) {
                    this.targetNoteDisplay.textContent = `${translate('Целевая нота:')} ${stringNote}`;
                }
                if (this.deviationDisplay) {
                    this.deviationDisplay.textContent = `${translate('Отклонение:')} --`;
                    this.deviationDisplay.style.color = '#4fc3f7';
                }
            }
            // Сбрасываем визуальный индикатор отклонения
            if (this.accuracyFillLeft) this.accuracyFillLeft.style.width = '0';
            if (this.accuracyFillRight) this.accuracyFillRight.style.width = '0';
            return;
        }

        if (!targetFrequency || !detectedFreq || detectedFreq <= 0) {
            // Нет сигнала – очищаем полосу отклонения
            if (this.accuracyFillLeft) this.accuracyFillLeft.style.width = '0';
            if (this.accuracyFillRight) this.accuracyFillRight.style.width = '0';
            if (this.targetNoteDisplay) {
                if (this.selectedStringIndex === null) {
                    this.targetNoteDisplay.textContent = translate('Режим: Автоматический');
                } else {
                    this.targetNoteDisplay.textContent = `${translate('Целевая нота:')} --`;
                }
            }
            if (this.deviationDisplay) {
                this.deviationDisplay.textContent = 'Отклонение: --';
                this.deviationDisplay.style.color = '#4fc3f7';
            }

            // Сбрасываем подсветку предложенной струны в авто-режиме
            if (this.autoMode && this.stringButtons) {
                this.stringButtons.forEach(btn => btn.classList.remove('suggested'));
            }
            return;
        }

        const cents = 1200 * Math.log2(detectedFreq / targetFrequency);
        const absCents = Math.abs(cents);
        const maxCents = 50; // предел для визуализации (половина шкалы)
        const clamped = Math.min(absCents, maxCents);
        const fillPercent = (clamped / maxCents) * 50; // 0–50% от всей ширины (половина бара)

        // Определяем цвет по степени отклонения
        let fillColor = '#69f0ae'; // почти в строю
        let glowColor = 'rgba(105, 240, 174, 0.8)';
        if (absCents >= 5 && absCents < 20) {
            fillColor = '#FFEB3B';
            glowColor = 'rgba(255, 235, 59, 0.8)';
        } else if (absCents >= 20) {
            fillColor = '#FF5252';
            glowColor = 'rgba(255, 82, 82, 0.9)';
        }

        // Заполняем полосу слева или справа от центра
        if (this.accuracyFillLeft && this.accuracyFillRight) {
            if (cents > 0) {
                // Нота выше цели – заполняем правую половину
                this.accuracyFillRight.style.width = `${fillPercent}%`;
                this.accuracyFillRight.style.backgroundColor = fillColor;
                this.accuracyFillRight.style.boxShadow = `0 0 12px ${glowColor}`;

                this.accuracyFillLeft.style.width = '0';
                this.accuracyFillLeft.style.boxShadow = 'none';
            } else if (cents < 0) {
                // Нота ниже цели – заполняем левую половину
                this.accuracyFillLeft.style.width = `${fillPercent}%`;
                this.accuracyFillLeft.style.backgroundColor = fillColor;
                this.accuracyFillLeft.style.boxShadow = `0 0 12px ${glowColor}`;

                this.accuracyFillRight.style.width = '0';
                this.accuracyFillRight.style.boxShadow = 'none';
            } else {
                // Точно в ноте – обе половины пустые
                this.accuracyFillLeft.style.width = '0';
                this.accuracyFillRight.style.width = '0';
                this.accuracyFillLeft.style.boxShadow = 'none';
                this.accuracyFillRight.style.boxShadow = 'none';
            }
        }

        // Обновляем отображение целевой ноты
        if (this.targetNoteDisplay) {
            if (this.selectedStringIndex === null) {
                // Автоматический режим - показываем ближайшую ноту
                const noteInfo = this.frequencyToNote(detectedFreq);
                this.targetNoteDisplay.textContent = `${translate('Ближайшая нота:')} ${noteInfo.name}`;
            } else if (targetNoteName) {
                this.targetNoteDisplay.textContent = `${translate('Целевая нота:')} ${targetNoteName}`;
            } else {
                // Если нота не указана, вычисляем её из частоты
                const noteInfo = this.frequencyToNote(targetFrequency);
                this.targetNoteDisplay.textContent = `${translate('Целевая нота:')} ${noteInfo.name}`;
            }
        }

        // В авто-режиме определяем ближайшую по частоте струну и подсвечиваем кнопку,
        // а также фиксируем настройку (галочка), если достаточно долго в строю.
        const settings = getCurrentSettings();
        const stringNotes = settings.stringNotes || ['E4', 'B3', 'G3', 'D3', 'A2', 'E2'];
        let nearestStringIndex = null;
        let nearestStringCents = null;

        if (Array.isArray(stringNotes) && stringNotes.length > 0 && detectedFreq > 0) {
            for (let i = 0; i < stringNotes.length; i++) {
                const freq = noteNameToFrequency(stringNotes[i]);
                if (!freq || freq <= 0) continue;
                const stringCents = 1200 * Math.log2(detectedFreq / freq);
                const absStringCents = Math.abs(stringCents);

                if (nearestStringCents === null || absStringCents < nearestStringCents) {
                    nearestStringCents = absStringCents;
                    nearestStringIndex = i;
                }
            }
        }

        // Подсветка предложенной струны в авто-режиме
        if (this.autoMode && this.stringButtons && nearestStringIndex !== null) {
            this.stringButtons.forEach(btn => {
                const idx = parseInt(btn.dataset.stringIndex, 10);
                btn.classList.toggle('suggested', idx === nearestStringIndex);
            });
        }

        // Логика "струна настроена" по стабильному попаданию в диапазон
        if (nearestStringIndex !== null) {
            // Диапазон фиксации расширен до жёлтой зоны (совпадает с границей < 20 центов)
            const TUNED_THRESHOLD_CENTS = 20;      // насколько точно нужно попасть
            const STABLE_FRAMES_REQUIRED = 20;     // сколько кадров подряд нужно удерживать строй

            if (nearestStringCents < TUNED_THRESHOLD_CENTS) {
                this.stringStableCounters[nearestStringIndex] = (this.stringStableCounters[nearestStringIndex] || 0) + 1;
            } else {
                this.stringStableCounters[nearestStringIndex] = 0;
            }

            if (this.stringStableCounters[nearestStringIndex] >= STABLE_FRAMES_REQUIRED) {
                this.tunedStrings.add(nearestStringIndex);
                if (this.stringButtons) {
                    this.stringButtons.forEach(btn => {
                        const idx = parseInt(btn.dataset.stringIndex, 10);
                        btn.classList.toggle('tuned', this.tunedStrings.has(idx));
                    });
                }
            }
        }

        // Обновляем отображение отклонения
        if (this.deviationDisplay) {
            const direction = cents > 0 ? 'выше' : (cents < 0 ? 'ниже' : '');
            const deviationText = absCents < 0.1 
                ? 'В строю!' 
                : `${absCents.toFixed(1)} центов ${direction}`;
            this.deviationDisplay.textContent = `${translate('Отклонение:')} ${deviationText}`;
            
            // Цвет отклонения
            if (absCents < 5) {
                this.deviationDisplay.style.color = '#69f0ae';
            } else if (absCents < 20) {
                this.deviationDisplay.style.color = '#FFEB3B';
            } else {
                this.deviationDisplay.style.color = '#FF5722';
            }
        }
    }

    // Получение стабильной ноты с использованием гистерезиса
    getStableNote(frequency) {
        const currentNoteInfo = this.frequencyToNote(frequency);
        const currentNoteName = currentNoteInfo.name;
        
        // Добавляем текущую ноту в историю
        this.noteHistory.push(currentNoteName);
        if (this.noteHistory.length > 15) { // История на 15 последних определений
            this.noteHistory.shift();
        }
        
        // Подсчитываем частоту каждой ноты в истории
        const noteCounts = {};
        this.noteHistory.forEach(note => {
            noteCounts[note] = (noteCounts[note] || 0) + 1;
        });
        
        // Находим наиболее часто встречающуюся ноту
        let mostCommonNote = currentNoteName;
        let maxCount = 0;
        for (const [note, count] of Object.entries(noteCounts)) {
            if (count > maxCount) {
                maxCount = count;
                mostCommonNote = note;
            }
        }
        
        // Если текущая нота совпадает с наиболее частой, увеличиваем счетчик стабильности
        if (currentNoteName === mostCommonNote) {
            this.noteStabilityCounter++;
        } else {
            // Если нота изменилась, проверяем, достаточно ли она стабильна
            const stabilityRatio = maxCount / this.noteHistory.length;
            if (stabilityRatio >= this.NOTE_STABILITY_THRESHOLD) {
                // Новая нота достаточно стабильна - переключаемся
                this.lastStableNote = mostCommonNote;
                this.noteStabilityCounter = maxCount;
            } else {
                // Новая нота недостаточно стабильна - используем предыдущую стабильную
                if (this.lastStableNote) {
                    mostCommonNote = this.lastStableNote;
                }
            }
        }
        
        // Если текущая нота стабильна, обновляем lastStableNote
        if (this.noteStabilityCounter >= 10 && currentNoteName === mostCommonNote) {
            this.lastStableNote = mostCommonNote;
        }
        
        // Получаем информацию о стабильной ноте
        if (mostCommonNote !== currentNoteName) {
            // Если используем другую ноту, пересчитываем частоту
            return this.frequencyToNote(noteNameToFrequency(mostCommonNote));
        }
        
        return currentNoteInfo;
    }

    // Преобразование частоты в ноту
    frequencyToNote(frequency) {
        if (typeof frequency !== 'number' || frequency <= 0) {
            return {
                name: '--',
                frequency: 0
            };
        }

        const A4 = 440;
        const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

        const noteNumber = 12 * Math.log2(frequency / A4) + 69;
        const roundedNoteNumber = Math.round(noteNumber);

        let correctedFrequency = frequency;
        let octaveCorrection = 0;

        // Для низких частот (< 100 Гц) проверяем, не является ли это октавой ниже
        if (frequency < 100) {
            const doubleFreq = frequency * 2;
            const doubleNoteNumber = 12 * Math.log2(doubleFreq / A4) + 69;
            const doubleRounded = Math.round(doubleNoteNumber);

            if (Math.abs(doubleNoteNumber - doubleRounded) < Math.abs(noteNumber - roundedNoteNumber)) {
                correctedFrequency = doubleFreq;
                octaveCorrection = -1;
            }
        }
        
        // Для высоких частот (> 400 Гц) проверяем, не является ли это октавой выше
        // Если частота примерно в два раза меньше ожидаемой для высоких нот, умножаем на 2
        if (frequency > 400 && frequency < 800) {
            const doubleFreq = frequency * 2;
            const doubleNoteNumber = 12 * Math.log2(doubleFreq / A4) + 69;
            const doubleRounded = Math.round(doubleNoteNumber);
            const doubleNoteDiff = Math.abs(doubleNoteNumber - doubleRounded);
            const currentNoteDiff = Math.abs(noteNumber - roundedNoteNumber);
            
            // Если удвоенная частота дает более точное попадание в ноту, используем её
            if (doubleNoteDiff < currentNoteDiff && doubleNoteDiff < 0.3) {
                correctedFrequency = doubleFreq;
                octaveCorrection = 1;
            }
        }

        const correctedNoteNumber = 12 * Math.log2(correctedFrequency / A4) + 69;
        const finalNoteNumber = Math.round(correctedNoteNumber);

        let indexInOctave = finalNoteNumber % 12;
        if (indexInOctave < 0) {
            indexInOctave += 12;
        }

        const octave = Math.floor(finalNoteNumber / 12) - 1 + octaveCorrection;
        const noteName = noteNames[indexInOctave] + octave;

        const expectedFrequency = A4 * Math.pow(2, (finalNoteNumber - 69) / 12);

        return {
            name: noteName,
            frequency: frequency,
            expectedFrequency: expectedFrequency
        };
    }

    // Нормализация частоты к ожидаемой: подбираем множитель 2^k,
    // чтобы приблизить детектированную частоту к целевой (исправляем ошибки октавы/гармоники).
    normalizeFrequencyToTarget(detectedFreq, targetFreq) {
        if (!detectedFreq || !targetFreq || detectedFreq <= 0 || targetFreq <= 0) {
            return detectedFreq;
        }

        let bestFreq = detectedFreq;
        let bestDiff = Math.abs(Math.log2(detectedFreq / targetFreq));

        // Проверяем несколько соседних октав/подоктав (увеличиваем диапазон для высоких частот)
        // Для высоких частот проверяем больше октав, так как гармоники могут быть сильнее
        // Особенно важно проверять октавы ВВЕРХ для высоких нот (F5, G5 и т.д.)
        const maxOctaves = targetFreq > 400 ? 4 : 3; // Увеличено до 4 для высоких частот
        for (let k = -maxOctaves; k <= maxOctaves; k++) {
            const factor = Math.pow(2, k);
            const cand = detectedFreq * factor;
            if (cand <= 0) continue;

            const diff = Math.abs(Math.log2(cand / targetFreq));
            if (diff < bestDiff) {
                bestDiff = diff;
                bestFreq = cand;
            }
        }

        // Дополнительная проверка: если частота примерно в два раза меньше целевой,
        // и целевая частота высокая (>500 Гц), скорее всего это гармоника
        if (targetFreq > 500 && detectedFreq < targetFreq * 0.6 && detectedFreq > targetFreq * 0.4) {
            const doubledFreq = detectedFreq * 2;
            const doubledDiff = Math.abs(Math.log2(doubledFreq / targetFreq));
            if (doubledDiff < bestDiff) {
                bestFreq = doubledFreq;
            }
        }

        return bestFreq;
    }

    // Остановка аудио
    stopAudio() {
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
        }
        
        if (this.checkIntervalId) {
            clearTimeout(this.checkIntervalId);
            this.checkIntervalId = null;
        }

        if (this.microphone && this.microphone.mediaStream) {
            this.microphone.mediaStream.getTracks().forEach(track => track.stop());
        }

        if (this.microphone) {
            this.microphone.disconnect();
        }

        if (this.monitorGain) {
            this.monitorGain.disconnect();
        }

        if (this.analyser) {
            this.analyser.disconnect();
        }

        disconnectEffects();

        this.isListening = false;
        this.micBtn.disabled = false;
        this.stopBtn.disabled = true;
        this.frequencyDisplay.textContent = '-- Hz';
        this.noteDisplay.textContent = '--';

        stopAllAudio();
    }

    updateMonitorSetting(enable) {
        if (!this.microphone || !this.monitorGain || !this.audioContext) return;

        this.microphone.disconnect();
        this.monitorGain.disconnect();

        this.microphone.connect(this.analyser);

        if (enable) {
            this.microphone.connect(this.monitorGain).connect(this.audioContext.destination);
        }
    }

    updateDisableEffectsSetting(disable) {
        // Этот метод работает только в режиме настроек
        if (this.gameMode) return;

        if (guitarEffects && guitarEffects.toggleEffects) {
            // Если disable = true, отключаем эффекты (чистый звук)
            // Если disable = false, включаем эффекты
            guitarEffects.toggleEffects(!disable);
            console.log(`Effects ${disable ? 'disabled' : 'enabled'} in settings mode`);
        }
    }

    detectChord(playedFrequencies, expectedNotes, toleranceCents = 80) {
        if (!expectedNotes || !Array.isArray(expectedNotes) || expectedNotes.length === 0) {
            return {
                isChord: true,
                isCorrect: false,
                matchedNotes: [],
                playedFrequencies: playedFrequencies,
                expectedFrequencies: []
            };
        }

        const expectedFrequencies = expectedNotes
            .map(note => note && note.frequency ? note.frequency : null)
            .filter(freq => freq !== null);

        if (expectedFrequencies.length === 0) {
            return {
                isChord: true,
                isCorrect: false,
                matchedNotes: [],
                playedFrequencies: playedFrequencies,
                expectedFrequencies: []
            };
        }

        const matchedNotes = expectedFrequencies.map(expected => {
            return playedFrequencies.some(played => {
                if (!played || !expected || played <= 0 || expected <= 0) {
                    return false;
                }
                // Используем допуск в центах для более точного определения
                const cents = 1200 * Math.log2(played / expected);
                return Math.abs(cents) < toleranceCents;
            });
        });

        // Вместо требования идеального совпадения всех нот аккорда
        // считаем аккорд корректным, если совпала большая часть нот.
        const totalNotes = matchedNotes.length;
        const matchedCount = matchedNotes.filter(Boolean).length;
        const minRequiredMatches = Math.max(1, Math.round(totalNotes * 0.6));
        const isFullChord = matchedCount >= minRequiredMatches;

        return {
            isChord: true,
            isCorrect: isFullChord,
            matchedNotes: matchedNotes,
            playedFrequencies: playedFrequencies,
            expectedFrequencies: expectedFrequencies
        };
    }

    // НОВЫЙ МЕТОД: быстрый и точный YIN‑подобный анализатор высоты тона
    getFrequencyAutocorrelation(data, sampleRate) {
        const N = data.length;
        if (N < 128) return 0; // минимальный размер для работы

        // Копируем и убираем DC‑сдвиг
        const buf = new Float32Array(N);
        let mean = 0;
        for (let i = 0; i < N; i++) {
            buf[i] = data[i];
            mean += buf[i];
        }
        mean /= N;

        let energy = 0;
        for (let i = 0; i < N; i++) {
            const v = buf[i] - mean;
            buf[i] = v;
            energy += v * v;
        }
        const rms = Math.sqrt(energy / N);
        if (rms < 0.005) return 0; // слишком тихо – считаем тишиной

        // Лёгкое окномирование (Hann), чтобы стабилизировать результат
        for (let i = 0; i < N; i++) {
            const w = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (N - 1)));
            buf[i] *= w;
        }

        // Для маленьких буферов (<=512) не делаем децимацию, чтобы сохранить точность
        // Для больших буферов децимируем для ускорения
        let decimation = 1;
        let M = N;
        let x = buf;
        
        if (N > 512) {
            decimation = 2;
            M = (N / decimation) | 0;
            x = new Float32Array(M);
            for (let i = 0; i < M; i++) {
                const idx = i * decimation;
                x[i] = 0.5 * (buf[idx] + (idx + 1 < N ? buf[idx + 1] : 0));
            }
        }

        const fs = sampleRate / decimation;

        // Диапазон лагов для гитары: примерно 60–1500 Гц (увеличено для высоких нот)
        // Для маленьких буферов ограничиваем максимальную частоту, чтобы хватило данных
        const fMin = 60;
        const fMax = N <= 512 ? 1000 : 1500; // Увеличено до 1500 для поддержки высоких нот (F5, G5 и т.д.)
        const tauMin = Math.max(2, Math.floor(fs / fMax));
        const tauMax = Math.min((fs / fMin) | 0, M - 3);
        if (tauMax <= tauMin + 2) return 0;

        const yin = new Float32Array(tauMax + 1);

        // Шаг по выборкам адаптируем под скорость игры
        // Для маленьких буферов используем шаг 1 для максимальной точности
        const speedFactor = (this.gameMode && typeof getSpeedFactor === 'function') ? getSpeedFactor() : 0;
        const sampleStep = (N <= 512) ? 1 : (speedFactor >= 0.75 ? 3 : (speedFactor > 0.5 ? 2 : 1));

        // Difference function d(tau) - для маленьких буферов используем все сэмплы
        for (let tau = 1; tau <= tauMax; tau++) {
            let sum = 0;
            const limit = M - tau;
            let count = 0;
            for (let i = 0; i < limit; i += sampleStep) {
                const d = x[i] - x[i + tau];
                sum += d * d;
                count++;
            }
            yin[tau] = count > 0 ? sum / count : 0;
        }

        // Кумулятивно-нормированная разность d'(tau)
        let runningSum = 0;
        let bestTau = -1;
        let bestValue = 1;
        const threshold = 0.12; // более строгий порог для лучшего выделения фундамента
        
        // Собираем все локальные минимумы для выбора лучшего
        const candidates = [];

        // Сначала ищем все локальные минимумы ниже порога
        for (let tau = tauMin; tau <= tauMax; tau++) {
            const d = yin[tau];
            runningSum += d;
            const cmnd = runningSum > 0 ? (d * tau) / runningSum : 1;
            yin[tau] = cmnd;

            // Проверяем, что это локальный минимум (меньше соседей) и ниже порога
            if (tau > tauMin + 2 && tau < tauMax - 1) {
                const isLocalMin = cmnd < yin[tau - 1] && cmnd < yin[tau + 1];
                if (isLocalMin && cmnd < threshold) {
                    const freq = fs / tau;
                    candidates.push({
                        tau: tau,
                        value: cmnd,
                        frequency: freq
                    });
                }
            }

            // Также отслеживаем глобальный минимум на случай, если порог не сработал
            if (cmnd < bestValue) {
                bestValue = cmnd;
                bestTau = tau;
            }
        }
        
        // Если есть кандидаты, выбираем лучший
        if (candidates.length > 0) {
            // Сортируем по качеству (меньше значение = лучше), но для высоких частот (>400 Гц)
            // предпочитаем более высокие частоты (чтобы избежать определения гармоники)
            candidates.sort((a, b) => {
                // Сначала по качеству (меньше значение = лучше)
                const qualityDiff = a.value - b.value;
                // Если качество очень похожее (разница < 0.05), предпочитаем более высокую частоту
                if (Math.abs(qualityDiff) < 0.05 && a.frequency > 400) {
                    return b.frequency - a.frequency; // Предпочитаем более высокую частоту
                }
                return qualityDiff;
            });
            
            bestTau = candidates[0].tau;
            bestValue = candidates[0].value;
        }

        if (bestTau < 0) return 0;

        // Улучшенная параболическая интерполяция вокруг лучшего tau
        // Для маленьких буферов это критично для точности
        const k = bestTau;
        let refinedTau = k;
        
        // Более точная интерполяция с проверкой границ
        if (k > 1 && k < tauMax) {
            const prev = yin[k - 1];
            const curr = yin[k];
            const next = yin[k + 1];
            
            // Параболическая интерполяция: находим минимум параболы через три точки
            const denom = 2 * (2 * curr - prev - next);
            if (Math.abs(denom) > 1e-10) {
                const delta = (prev - next) / denom;
                // Ограничиваем дельту, чтобы не уйти слишком далеко
                const clampedDelta = Math.max(-0.5, Math.min(0.5, delta));
                refinedTau = k + clampedDelta;
            }
        }

        const freq = fs / refinedTau;
        // Увеличиваем максимальную частоту до 3000 Гц для поддержки очень высоких нот
        if (!isFinite(freq) || freq <= 0 || freq > 3000) {
            return 0;
        }

        return freq;
    }

    // ОБНОВЛЕННЫЙ МЕТОД анализа высоты тона
    analyzePitch(analyser, sensitivity, callback) {
        const bufferLength = analyser.fftSize;
        const timeData = new Float32Array(bufferLength);
        const freqData = new Uint8Array(analyser.frequencyBinCount);

        // Получаем данные во временной области для автокорреляции
        analyser.getFloatTimeDomainData(timeData);

        // И частотные данные для дополнительной проверки
        analyser.getByteFrequencyData(freqData);

        // Используем автокорреляцию для точного определения основной частоты
        const frequency = this.getFrequencyAutocorrelation(timeData, this.audioContext.sampleRate);

        // Увеличиваем лимит до 2000 Гц для поддержки высоких нот на первых струнах
        if (frequency > 0 && frequency < 2000) {
            callback({
                frequency: frequency,
                noteName: this.frequencyToNote(frequency).name
            });
        } else {
            // Fallback: используем старый метод, если автокорреляция не сработала
            const strongFrequencies = this.detectStrongFrequencies(analyser, sensitivity, 3);

            if (strongFrequencies.length === 0) {
                callback(null);
                return;
            }

            let dominant = strongFrequencies[0];
            for (let i = 1; i < strongFrequencies.length; i++) {
                if (strongFrequencies[i].magnitude > dominant.magnitude) {
                    dominant = strongFrequencies[i];
                }
            }

            const minMagnitude = 30 + 100 * (1 - sensitivity);

            if (dominant.magnitude < minMagnitude) {
                callback(null);
                return;
            }

            callback({
                frequency: dominant.freq,
                noteName: this.frequencyToNote(dominant.freq).name
            });
        }
    }

    // Обновленная функция detectStrongFrequencies
    detectStrongFrequencies(analyser, sensitivity, count = 3) {
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyser.getByteFrequencyData(dataArray);

        const frequencies = [];
        const minFrequency = 40;
        // Увеличиваем максимальную частоту до 3000 Гц для поддержки очень высоких нот
        const maxFrequency = 3000;

        const sampleRate = this.audioContext.sampleRate;
        const binSize = sampleRate / analyser.fftSize;
        const minIndex = Math.max(1, Math.floor(minFrequency / binSize));
        const maxIndex = Math.min(Math.floor(maxFrequency / binSize), bufferLength - 2);

        const baseThreshold = 30 + 100 * (1 - sensitivity);

        const peaks = [];

        for (let i = minIndex; i <= maxIndex; i++) {
            const currentFreq = i * binSize;

            const dynamicThreshold = currentFreq < 150 ?
                baseThreshold - 10 :
                baseThreshold;

            if (dataArray[i] > dataArray[i-1] &&
                dataArray[i] > dataArray[i+1] &&
                dataArray[i] > dynamicThreshold) {

                peaks.push({
                    index: i,
                    magnitude: dataArray[i],
                    freq: currentFreq
                });
            }
        }

        peaks.sort((a, b) => b.magnitude - a.magnitude);

        for (const peak of peaks) {
            const interpolatedIndex = this.getMaxFrequencyIndex(dataArray, peak.index);
            const freq = interpolatedIndex * binSize;

            if (freq < minFrequency || freq > maxFrequency) continue;

            frequencies.push({
                freq: freq,
                magnitude: peak.magnitude
            });

            if (frequencies.length >= count) break;
        }

        return frequencies;
    }

    // Функция интерполяции пиков
    getMaxFrequencyIndex(dataArray, centerIndex) {
        const left = dataArray[centerIndex - 1];
        const center = dataArray[centerIndex];
        const right = dataArray[centerIndex + 1];

        if (left < center && center > right) {
            const delta = (left - right) / (2 * (left - 2 * center + right));
            return centerIndex + delta;
        }

        return centerIndex;
    }

    // ОБНОВЛЕННЫЙ МЕТОД analyzeNotes для игрового режима
    analyzeNotes(analyser, sensitivity, callback) {
        const bufferLength = analyser.fftSize;
        const timeData = new Float32Array(bufferLength);
        const freqData = new Uint8Array(analyser.frequencyBinCount);

        // Получаем данные для анализа
        analyser.getFloatTimeDomainData(timeData);
        analyser.getByteFrequencyData(freqData);

        let sumSquares = 0;
        for (let i = 0; i < bufferLength; i++) {
            const sample = timeData[i];
            sumSquares += sample * sample;
        }
        this.lastSignalLevel = Math.sqrt(sumSquares / bufferLength);

        if (isCurrentChord()) {
            // Для аккордов используем FFT-анализ для обнаружения нескольких частот
            const strongFreqObjects = this.detectStrongFrequencies(analyser, sensitivity, 6); // Увеличиваем количество для аккордов
            const strongFrequencies = strongFreqObjects.map(obj => obj.freq);

            const currentChord = getCurrentNote();
            const result = this.detectChord(strongFrequencies, currentChord);
            callback(result);
        } else {
            // Для одиночных нот используем автокорреляцию для большей точности
            const frequency = this.getFrequencyAutocorrelation(timeData, this.audioContext.sampleRate);

            // Увеличиваем лимит до 2000 Гц для поддержки высоких нот на первых струнах
            if (frequency > 0 && frequency < 2000) {
                const noteInfo = this.frequencyToNote(frequency);
                callback({
                    frequency: frequency,
                    noteName: noteInfo.name,
                    isChord: false,
                    isCorrect: false // Определяется позже в checkSound
                });
            } else {
                // Fallback на FFT-анализ, если автокорреляция не сработала
                const strongFreqObjects = this.detectStrongFrequencies(analyser, sensitivity, 1);
                if (strongFreqObjects.length > 0) {
                    const dominantFrequency = strongFreqObjects[0].freq;
                    const noteInfo = this.frequencyToNote(dominantFrequency);
                    callback({
                        frequency: dominantFrequency,
                        noteName: noteInfo.name,
                        isChord: false,
                        isCorrect: false
                    });
                } else {
                    callback(null);
                }
            }
        }
    }

    isNoteMatch(detectedFreq, expectedFreq) {
        if (!detectedFreq || !expectedFreq || detectedFreq <= 0 || expectedFreq <= 0) {
            return false;
        }
        
        // Используем допуск в центах (полутонах) для более точного определения
        // 50 центов = примерно полтона, что является разумным допуском для гитары
        const cents = 1200 * Math.log2(detectedFreq / expectedFreq);
        const toleranceCents = 50; // Допуск в центах
        
        return Math.abs(cents) < toleranceCents;
    }

    setListening(value) {
        this.isListening = value;
    }

    resumeListening() {
        this.isListening = true;
        this.currentNoteId = -1; // Сбрасываем ID текущей ноты
    }

    resetNoteDetection() {
        this.currentNoteId = -1;
        this.isListening = true;
        this.awaitingSilence = true; // Ждем новый звук (тишину перед следующей нотой)
        this.mustAwaitSilence = true; // Принудительно требуем тишину (игнорируем оптимизации)
    }

    isActive() {
        return this.isListening;
    }

    async restartWithNewDevice() {
        if (this.isListening) {
            // Сохраняем текущий режим (игровой или обычный)
            const wasGameMode = this.gameMode;
            
            // Останавливаем текущий микрофон
            this.stopAudio();
            
            // Небольшая задержка для завершения остановки
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Перезапускаем с новым устройством в том же режиме
            const settings = getCurrentSettings();
            await this.initAudio(settings, wasGameMode);
        }
    }
}

// Создаем и экспортируем экземпляр с привязкой контекста
const tuner = new Tuner();

// Экспортируем методы с явной привязкой контекста
export const initTuner = tuner.initTuner.bind(tuner);
export const stopAudio = tuner.stopAudio.bind(tuner);
export const resetCurrentNoteId = tuner.resetCurrentNoteId.bind(tuner);
export const updateMonitorSetting = tuner.updateMonitorSetting.bind(tuner);
export const updateDisableEffectsSetting = tuner.updateDisableEffectsSetting.bind(tuner);
export const setListening = tuner.setListening.bind(tuner);
export const resumeListening = tuner.resumeListening.bind(tuner);
export const resetNoteDetection = tuner.resetNoteDetection.bind(tuner);
export const isTunerActive = tuner.isActive.bind(tuner);
export const restartTunerWithNewDevice = tuner.restartWithNewDevice.bind(tuner);